import { FC, useCallback, memo } from 'react';
// import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { UserCheck, UserPlus, Share2 } from 'lucide-react';

import { useModal } from '@hooks/useModal';
import { useDownloadAppModal } from '@hooks/useDownloadAppModal';
import { useShareModal } from '@hooks/useShareModal';
import { useUserDataById } from '@redux/selectors/users';
import { generateProfileShareData } from '@utils/share';
import theme from 'theme';
import SkewedButton from '@components/buttons/SkewedButton';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import { DefaultText } from '@components/text';
import Modal from '@components/modal';
import reportIcon from '@assets/icons/report.png';

type Props = {
  userId: string;
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  width: 100%;
`;

const FollowButton = styled(SkewedButton)<{ $isFollowing?: boolean }>`
  flex: 1;
  min-height: 48px;
  margin-right: 4px;

  ${({ $isFollowing, theme }) =>
    $isFollowing &&
    `
    background: ${theme.colors.grey[800]};
    border: 0.5px solid ${theme.colors.grey[600]};
  `}
`;

const IconButton = styled(TouchableOpacity)`
  flex: 0;
  min-height: 48px;
  display: flex;
  flex-direction: row;
  position: relative;
  justify-content: center;
  align-items: center;
  padding: 8px 12px;
  margin: 0 4px;
  background-color: ${({ theme }) =>
    `${theme.colors.black}${theme.opacity[65]}`};
  border-radius: 8px;
  border: 0.5px solid ${({ theme }) => theme.colors.grey[600]};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) =>
      `${theme.colors.black}${theme.opacity[15]}`};
    border-color: ${({ theme }) => theme.colors.grey[500]};
  }
`;

const ButtonIcon = styled.div`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const ConfirmationModal = styled(Modal)`
  .modal-content {
    max-width: 400px;
  }
`;

const ConfirmationText = styled(DefaultText)`
  text-align: center;
  margin-bottom: 16px;
  font-size: ${({ theme }) => theme.fonts.fontSize.base};
`;

const UserProfileButtons: FC<Props> = ({ userId }) => {
  const user = useUserDataById(userId);
  //   const navigate = useNavigate();
  const { isModalShown, showModal, hideModal } = useModal();
  const { openDownloadAppModal } = useDownloadAppModal();
  const { openShareModal } = useShareModal();

  // TODO: Implement follow functionality when backend is ready
  const amIFollowingUser = false;
  const isFollowLoading = false;
  const isUnfollowLoading = false;

  const onFollowPress = useCallback(() => {
    // TODO: Implement follow functionality
    openDownloadAppModal();
  }, [openDownloadAppModal]);

  const onUnfollowPress = useCallback(() => {
    showModal();
  }, [showModal]);

  const confirmUnfollow = useCallback(() => {
    // TODO: Implement unfollow functionality
    hideModal();
  }, [hideModal]);

  const onSharePress = useCallback(() => {
    if (userId) {
      const shareData = generateProfileShareData(userId);
      openShareModal(shareData);
    }
  }, [userId, openShareModal]);

  const onReportPress = useCallback(() => {
    // TODO: Implement report functionality
    openDownloadAppModal();
  }, [openDownloadAppModal]);

  return (
    <>
      <Container>
        {amIFollowingUser ? (
          <FollowButton
            isGradient={amIFollowingUser}
            onClick={onUnfollowPress}
            disabled={isUnfollowLoading}
            isLoading={isUnfollowLoading}
            icon={<UserCheck size={24} />}
          >
            Following
          </FollowButton>
        ) : (
          <FollowButton
            onClick={onFollowPress}
            disabled={isFollowLoading}
            isLoading={isFollowLoading}
            icon={
              !isFollowLoading ? (
                <UserPlus size={24} color={theme.colors.white} />
              ) : undefined
            }
          >
            Follow
          </FollowButton>
        )}

        <IconButton onClick={onSharePress}>
          <ButtonIcon>
            <Share2 size={24} />
          </ButtonIcon>
        </IconButton>

        <IconButton onClick={onReportPress}>
          <ButtonIcon>
            <Icon src={reportIcon} />
          </ButtonIcon>
        </IconButton>
      </Container>

      <ConfirmationModal
        title='Unfollow User'
        isShown={isModalShown}
        onClose={hideModal}
        onConfirm={confirmUnfollow}
        isActionable={true}
      >
        <ConfirmationText>
          Are you sure you want to unfollow {user?.name}?
        </ConfirmationText>
      </ConfirmationModal>
    </>
  );
};

export default memo(UserProfileButtons);
